/**
 * The scenario form, grouped.
 *
 * A scenario is thin on purpose. It is a Cyber Academy course plus the configuration items the course
 * asks a learner to work through: the story - why PseudoCo needs any of this, what the learner is
 * meant to conclude - lives in the course, and duplicating it here would leave two versions to keep
 * in step. What stays are the knobs the runtime reads, and they group into two questions that are
 * genuinely different: what this section *is*, and how a run of it opens and closes.
 *
 * Extracted from the modal so "+ Add New" in the builder can open the same form in a slide-over
 * without leaving the tree.
 *
 * `demo_uid` and `demo_name` are not offered. A scenario used to be matched to a dCloud session on
 * its own demo id, and a scenario reused across four labs cannot be, because the labs have different
 * ids in different data centres. Publications carry that now, so the columns stay on the row - a run
 * opened through an old one is still honoured - but there is nothing here to author a new one with.
 */

import React from 'react';
import TextField from '@mui/material/TextField';
import { LabScenario, labSlugify } from '../../support/labService';
import { LAB_JSON_SCHEMAS } from '../../support/labSpecSchema';
import { LabCheckbox, LabFieldRow } from './LabShared';
import { LabSpecBuilder } from './LabSpecFields';
import { LabSection } from './LabAdminShared';
import LabCoursePicker from './LabCoursePicker';

/** A new scenario, with the runtime defaults the compile assumes. */
export const LAB_SCENARIO_EMPTY: Partial<LabScenario> = {
  name: '',
  slug: '',
  description: '',
  course_uid: null,
  course_name: '',
  version: 1,
  bootstrap_only: false,
  auto_start: false,
  setup_timeout_seconds: 1800,
  validation_interval_seconds: 60,
  pass_threshold_percent: 100,
  enabled: true,
  metadata: { customer: 'PseudoCo' },
};

/**
 * Why this scenario cannot be saved yet.
 *
 * Only the two that other rows depend on: something to call it, and a slug to derive from that,
 * because runs and injections record the slug and a row without one cannot be pointed at. Everything
 * else has a working default.
 */
export const labScenarioFormBlockers = (
  draft: Partial<LabScenario>,
  slug: string,
): string[] => {
  const blockers: string[] = [];

  if (!draft.name?.trim()) blockers.push('A name. It is what an author attaches the section by.');

  if (draft.name?.trim() && !slug) {
    blockers.push(
      'No slug could be derived from that name, and runs and injections refer to the scenario by it. Use letters or digits somewhere in the name.',
    );
  }

  return blockers;
};

export type LabScenarioFormProps = {
  scenario: LabScenario | null;
  draft: Partial<LabScenario>;
  onChange: (patch: Partial<LabScenario>) => void;
  /** Whether the form is on screen, so the course picker does not call Moodle from a closed panel. */
  active?: boolean;
  jsonError: string | null;
  onJsonError: (error: string | null) => void;
};

/** Derived from the name for a new scenario; frozen once saved, because runs point at it. */
export const labScenarioSlug = (
  scenario: LabScenario | null,
  draft: Partial<LabScenario>,
): string => (scenario ? scenario.slug : labSlugify(draft.name || ''));

const LabScenarioForm: React.FC<LabScenarioFormProps> = ({
  scenario,
  draft,
  onChange,
  active = true,
  jsonError,
  onJsonError,
}) => {
  return (
    <div className="flex flex-col gap-4">
      <LabSection
        title="What this section is"
        hint="Reusable: the same section is attached to every lab that needs it, so an edit here reaches all of them."
      >
        <TextField
          label="Name"
          value={draft.name || ''}
          onChange={(evt) => onChange({ name: evt.target.value })}
          helperText="What this section is called wherever it is attached."
          fullWidth
        />

        <TextField
          label="Description"
          value={draft.description || ''}
          onChange={(evt) => onChange({ description: evt.target.value })}
          helperText="One line for the admin lists. The learner reads the course, not this."
          multiline
          minRows={2}
          fullWidth
        />

        {/* The course is what makes the configuration mean anything: an item list says a VPN was
            brought up, and the course says why PseudoCo needed one. Only the id is stored. */}
        <LabCoursePicker
          key={scenario?.id ?? 'new'}
          courseUid={draft.course_uid}
          courseName={draft.course_name}
          active={active}
          onChange={onChange}
          unlinkedWarning="No course, so this section has no story attached to it: a learner gets the configuration and no account of what PseudoCo was trying to do. It still runs."
        />

        <LabFieldRow>
          <TextField
            label="Version"
            type="number"
            value={draft.version ?? 1}
            onChange={(evt) => onChange({ version: Number(evt.target.value) })}
            helperText="Bumped when the section changes enough that old runs are not comparable."
            fullWidth
          />
          <LabCheckbox
            label="Enabled"
            checked={!!draft.enabled}
            onChange={(enabled) => onChange({ enabled })}
            helpText="A disabled section opens no new runs, and compiles to nothing in every lab that has it attached. The attachments stay."
          />
        </LabFieldRow>
      </LabSection>

      <LabSection
        title="How a run of it behaves"
        hint="What happens once a session is matched: how long setup may take, how often validation is re-offered, and what counts as a pass."
      >
        <LabFieldRow columns={3}>
          <TextField
            label="Setup timeout (seconds)"
            type="number"
            value={draft.setup_timeout_seconds ?? 1800}
            onChange={(evt) => onChange({ setup_timeout_seconds: Number(evt.target.value) })}
            helperText="How long bootstrap and setup have before the run is abandoned."
            fullWidth
          />
          <TextField
            label="Validation interval (seconds)"
            type="number"
            value={draft.validation_interval_seconds ?? 60}
            onChange={(evt) => onChange({ validation_interval_seconds: Number(evt.target.value) })}
            helperText="How often recurring validation steps are re-offered to the agent."
            fullWidth
          />
          <TextField
            label="Pass threshold (%)"
            type="number"
            value={draft.pass_threshold_percent ?? 100}
            onChange={(evt) => onChange({ pass_threshold_percent: Number(evt.target.value) })}
            helperText="Weighted score needed to pass. Required criteria must be met regardless."
            fullWidth
          />
        </LabFieldRow>

        <LabCheckbox
          label="Start a run automatically"
          checked={!!draft.auto_start}
          onChange={(auto_start) => onChange({ auto_start })}
          helpText="A run opens as soon as a matching session goes active, rather than waiting for somebody to open one."
        />
        <LabCheckbox
          label="Bootstrap only"
          checked={!!draft.bootstrap_only}
          onChange={(bootstrap_only) => onChange({ bootstrap_only })}
          helpText="Prepare the pod and stop. No validation, no score - for a section that only has to make the environment exist."
        />
      </LabSection>

      <LabSpecBuilder
        schema={LAB_JSON_SCHEMAS.scenarioMetadata}
        value={draft.metadata}
        resetKey={scenario?.id ?? 'new'}
        onChange={(metadata, error) => {
          onJsonError(error);
          if (!error) onChange({ metadata });
        }}
      />

      {jsonError && <p className="m-0 text-sm text-amber-800">{`Metadata: ${jsonError}`}</p>}
    </div>
  );
};

export default LabScenarioForm;
